import secondFilterImg from '@/assets/images/secondFilter.svg';
import ImpTracker from '@/components/common/ImpTracker';
import Skeleton from '@/components/common/Skeleton';
import SortFilter from '@/components/common/SortFilter';
import { relativeLinkIfQAEnv } from '@/components/component-pool/tools/cms-link';
import Constants from '@/constants/const';
import { BrandMod, CategoryMod } from '@/constants/track';
import { useScrollData } from '@/hooks/useTouchData';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { CategoryType, CuisineCategory, FilterType, SecondCuisineCategory } from '@/types/category.d';
import { getCurrentParams, getPathNoLangOnClient, toString as queryToString } from '@/utils/url';
import { CheckmarkOutline } from '@weee-fe/kunlunicon';
import { isValidArray } from '@weee-fe/utilities';
import classnames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, MutableRefObject, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { getPageEventNameV2 } from 'tracks/tracker';
import NavTab, { ForwardRefableNavTab } from '../EnkiDummyUI/NavTab';
import { WeeeIcon } from '../Icon';
import ExplorerFilter from './ExplorerFilter';
import MainCategory from './MainCategory';

interface Props {
  mainCategoryList?: CuisineCategory[];
  /**当前分类 */
  mainCategory?: CuisineCategory | CategoryType;
  handleMainCategory?: (item: CuisineCategory | CategoryType, nextLink?: string) => void;
  secondCategoryList: SecondCuisineCategory[];
  /**当前二级分类 */
  secondCategory: SecondCuisineCategory;
  handleSecondCategory: (item: SecondCuisineCategory) => void;
  /**正在获取数据 */
  loading?: boolean;
  /**是否展示以及分类展位图 */
  showMainCategorySkeleton?: boolean;
  /**是否获取以及分类 */
  changeCategory?: boolean;
  /**是否加载二级分类 */
  secondLoading?: boolean;
  /**过滤数量 */
  filterCount: number;
  listRef: React.MutableRefObject<HTMLDivElement>;
  handleFilterClick?: any;
  showMainCategory?: boolean;
  buildPathName?: (item: ObjectType, isMainCategory: boolean) => string;
  sortFilterProps?: ObjectType;
  isFromBrand?: boolean;
  showShadow?: boolean;
  secondCategoryPreventDefault?: boolean;
  isNeedReplaceUrl?: boolean;
  filterBtnClassName?: string;
  trackCtx?: Record<string, string>;
  showBadgeOnFilter?: boolean;
  /**
   * 接口out_filters下发数据，用来渲染页面外部过滤项
   */
  outFilters: any[];
  /**
   * 已选择的外部过滤项，用来回显selected状态
   */
  selectedOutFilters: ObjectType;
  optimizeForGlobalHeader?: boolean;
  stickyL2Only?: boolean;
  bringSelectedAsFirstIfNeeded?: boolean;
  resetSubcategoriesScrollOnMainCatChange?: boolean;
  onCategoryClick?: (e: SyntheticEvent) => void;
  /**
   * 使用客户端模式
   * 分类页跳转不使用a链接，第一次访问是ssr，后边操作均为客户端
   * useClientMode为true，则一级、二级分类均为点击事件，不用Link
   */
  useClientMode?: boolean;
}

const SKELETONS = [1, 1, 1, 1, 1, 1];
const PIXEL_PERFECT_GUTTER_ALIGNMENT = 6; // pixels (Relative distance from left side of viewport to very beginning of GlobalNavBar left icons)

type ALinkProps = {
  href: string;
  ariaLabel: string;
  onClick: (url: string) => void;
  isNeedReplaceUrl: boolean;
};
const ALink: FC<ALinkProps> = ({ href, isNeedReplaceUrl, onClick, ariaLabel, children }) => {
  return (
    <Link href={href || ''} replace={isNeedReplaceUrl} shallow>
      <a className="flex flex-col items-center justify-center" onClick={() => onClick(href)} aria-label={ariaLabel}>
        {children}
      </a>
    </Link>
  );
};

function HorizontalSlideList(props: Props) {
  const {
    stickyL2Only,
    mainCategory,
    mainCategoryList,
    secondCategory,
    secondCategoryList = [],
    showShadow = false,
    changeCategory,
    filterCount,
    handleMainCategory,
    handleSecondCategory,
    listRef,
    showMainCategory = true,
    buildPathName,
    sortFilterProps,
    isFromBrand,
    isNeedReplaceUrl = false,
    filterBtnClassName,
    trackCtx,
    showBadgeOnFilter,
    outFilters,
    selectedOutFilters,
    optimizeForGlobalHeader,
    bringSelectedAsFirstIfNeeded,
    resetSubcategoriesScrollOnMainCatChange = false,
    onCategoryClick,
    useClientMode
  } = props;
  const sortFilterRef = useRef(null);
  const explorerFilterRef = useRef(null);
  const selectedNavbarUXRef = useRef(null);
  const [parentScrollWrapperCache, setParentScrollWrapperCache] = useState(null);

  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const { query, asPath } = router;
  const { scrolling, direction, position } = useScrollData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const secondCategoryRef = useRef<HTMLDivElement>(null);
  const activeIndex = useMemo(() => {
    return mainCategoryList.findIndex(item => {
      return item.key === mainCategory?.key;
    });
  }, [mainCategory?.key, JSON.stringify(mainCategoryList)]);
  const [loadingMain, setLoadingMain] = useState(true);
  const [selectExplorer, setSelectExplorer] = useState({});
  const { ellipseImpression, filterBtnImpression } = useImpressionT2();
  const { ellipseClick, filterBtnClick, customButtonClick } = useClickActionT2();
  useEffect(() => {
    if (optimizeForGlobalHeader || stickyL2Only) return;

    if (!scrolling || !direction.y) return;
    if (!categoryRef.current || !listRef.current) return;
    // 滑动过小不做变化
    if (position.y < 5) {
      categoryRef.current.style.transform = 'translateY(0px)';
      if (showShadow) categoryRef.current.style.boxShadow = '';
      listRef.current.style.transform = `translateY(0px)`;
      return;
    }
    if (direction.y === 'down') {
      categoryRef.current.style.transform = `translateY(-${scrollRef.current?.clientHeight}px)`;
      if (showShadow) categoryRef.current.style.boxShadow = '0px 1px 12px rgba(0, 0, 0, 0.12)';
      listRef.current.style.transform = `translateY(-${scrollRef.current?.clientHeight}px)`;
    }

    if (direction.y === 'up') {
      categoryRef.current.style.transform = 'translateY(0)';
      if (showShadow && position.y > 10) categoryRef.current.style.boxShadow = '0px 1px 12px rgba(0, 0, 0, 0.12)';
      listRef.current.style.transform = `translateY(0)`;
    }
  }, [direction.y, showShadow]);

  useEffect(() => {
    const currentSelectedIdx = secondCategoryList.findIndex(item => item.selected);
    if (currentSelectedIdx < 0) return;

    const thisItem = secondCategoryList[currentSelectedIdx];
    const elementId = `secondCategory-${thisItem.catalogue_num}`;

    const timeoutId = window.setTimeout(() => {
      const scrollRoot = secondCategoryRef.current;
      if (!scrollRoot?.isConnected) return;

      const escaped =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? `#${CSS.escape(elementId)}`
          : `[id="${String(elementId).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;

      const ele = scrollRoot.querySelector(escaped) as HTMLElement | null;
      if (!ele) return;

      ele.scrollIntoView({ block: 'nearest', inline: 'center' });
    });

    return () => window.clearTimeout(timeoutId);
  }, [JSON.stringify(secondCategoryList)]);

  useEffect(() => {
    setLoadingMain(false);
  }, []);

  const __getScrollWrapperIfNeeded = () => {
    if (!bringSelectedAsFirstIfNeeded) return null;

    if (parentScrollWrapperCache?.isConnected) return parentScrollWrapperCache;
    const domEl = document.querySelector(`#${Constants.NAV_CATEGORY_SCROLL_CONTAINER_ID}`);
    setParentScrollWrapperCache(domEl);
    return domEl;
  };

  useEffect(() => {
    if (!bringSelectedAsFirstIfNeeded) return null;

    if (selectedNavbarUXRef?.current) {
      const componentLevelFwd = selectedNavbarUXRef?.current;
      const scrollContainer =
        (parentScrollWrapperCache as HTMLElement) || (__getScrollWrapperIfNeeded() as HTMLElement);
      if (scrollContainer) {
        const componentLevelRef = componentLevelFwd?.containerRef as MutableRefObject<HTMLElement>;
        const wrapperEl = componentLevelRef?.current as HTMLElement;
        if (wrapperEl) {
          requestAnimationFrame(() => {
            if (wrapperEl?.isConnected) {
              const selectedRect = wrapperEl.getBoundingClientRect();
              const scrollContainerRect = scrollContainer.getBoundingClientRect();

              const viewportWidth = window.document.documentElement.clientWidth;
              const visibleAreaScrollStart = viewportWidth - scrollContainerRect.width; // size past the fixed menu/explore tab
              const currentScroll = scrollContainer.scrollLeft;
              const selectedOffset = selectedRect.x;

              // Calculate how much we need to adjust scroll to align selected element with visibleAreaScrollStart
              const adjustment = selectedOffset - visibleAreaScrollStart;
              scrollContainer.scrollLeft = currentScroll + adjustment - PIXEL_PERFECT_GUTTER_ALIGNMENT;
            }
          });
        }
      }
    }
  }, [bringSelectedAsFirstIfNeeded, mainCategory]);

  useEffect(() => {
    if (!!mainCategory) {
      secondCategoryRef?.current?.scrollTo(0, 0);
    }
  }, [resetSubcategoriesScrollOnMainCatChange, mainCategory]);

  const genCategoryHrefForPDPL2 = (cateKey, item?) => {
    const parent = cateKey.replace(/\d+/g, '');
    const hrefFromPdp = `/category/${cateKey}/${item.slug}?filter_sub_category=${parent}&filters=${JSON.stringify({
      catalogue_num: item.num
    })}`;
    return hrefFromPdp;
  };
  const genCategoryHref = (cateKey, isMainCategory, item?) => {
    let { sort, filters, slug, ...queries } = getCurrentParams();
    const { filterArr } = sortFilterProps;
    const newQuery: ObjectType = {
      ...queries,
      out_filters: {}
    };
    if (!!sort && !isMainCategory) newQuery.sort = sort;
    if (!filters) newQuery.filters = {};
    else {
      try {
        newQuery.filters = JSON.parse(filters as string);
      } catch (error) {
        newQuery.filters = {};
      }
    }
    let pathname = buildPathName?.(item, isMainCategory) || router.pathname;
    let fullUrl = '';
    if (isMainCategory) {
      if (pathname.startsWith('/cuisines')) {
        newQuery.filterCuisine = cateKey;
      } else if (pathname.startsWith('/category')) {
        newQuery.filter_sub_category = cateKey;
      }
      delete newQuery.filters.catalogue_num;
    } else {
      newQuery.filters.catalogue_num = cateKey;
    }
    if (Object.keys(newQuery.filters).length) {
      if (isMainCategory) {
        let _filter = {};
        Object.keys(newQuery.filters).forEach(key => {
          let isRemained = (filterArr || []).findIndex(item => item.shareable && item.property_key === key) > -1;
          if (isRemained) {
            _filter[key] = newQuery.filters[key];
          }
        });
        if (Object.keys(_filter).length) {
          newQuery.filters = JSON.stringify(_filter);
        } else {
          delete newQuery.filters;
        }
      } else {
        newQuery.filters = JSON.stringify(newQuery.filters);
      }
    } else {
      delete newQuery.filters;
    }
    fullUrl = `${pathname}${queryToString(newQuery)}`;
    return fullUrl;
  };

  const renderExplorerStore = () => {
    if (outFilters.length < 1) {
      return null;
    }
    return (
      <div className="flex items-center justify-start relative pl-4 pb-2">
        <div
          data-testid="btn-sub-category-filter"
          id="explorerCategoryFilter"
          className="z-20"
          onClick={() => {
            filterBtnClick({
              mod_pos: 2,
              co: {
                button_nm: 'filter_buttons',
                button_pos: 0
              },
              ctx: trackCtx
            });
            sortFilterRef.current?.show();
          }}>
          {changeCategory ? (
            <Skeleton.Button
              style={{ width: '32px', position: 'relative', height: '32px', top: 0, right: 0 }}
              shape="circle"
            />
          ) : (
            <div className="rounded-400 bg-surface-100-hairline px-2 h-[30px] flex items-center">
              <img className="w-4 h-4" src={secondFilterImg} alt="second filter image" />
            </div>
          )}
          {!changeCategory && filterCount > 0 && (
            <div className="w-2 h-2 rounded-full bg-surface-100-fg-default absolute top-1 left-10"></div>
          )}
        </div>
        {/* explorer type */}
        {changeCategory ? (
          <div className="box-border w-full flex overflow-x-scroll touch-auto -ml-2.5 pl-2 pr-4">
            <Skeleton.Button
              style={{
                width: '80px',
                position: 'relative',
                height: '32px',
                top: 0,
                right: 0,
                marginLeft: 12,
                borderRadius: 20
              }}
            />
          </div>
        ) : (
          <div className="box-border w-full flex overflow-x-scroll touch-auto -ml-2.5 pl-2 pr-4">
            {outFilters?.map((item, index) => {
              const currentSelectTagId = selectedOutFilters
                ? selectedOutFilters[item.property_key]?.map(item => item.value_key)?.toString()
                : '';
              const isBoolItem = item.property_show_type === FilterType.boolean;
              const isBoolItemSelected =
                item.property_values?.reduce((prev, curr) => {
                  return prev || !!curr?.selected;
                }, false) || currentSelectTagId === 'true';

              return (
                <ImpTracker
                  key={index}
                  impTrackingT2={() => {
                    filterBtnImpression({
                      mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                      mod_pos: 2,
                      co: {
                        button_nm: item.property_key,
                        button_pos: index,
                        // is_selected: secondCategory.catalogue_num === item.catalogue_num,
                        is_selected: !!currentSelectTagId,
                        url: router?.asPath
                      },
                      ctx: {
                        ...trackCtx,
                        tag_id: currentSelectTagId
                      }
                    });
                  }}>
                  <div
                    onClick={() => {
                      customButtonClick({
                        mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                        mod_pos: 2,
                        co: {
                          target_nm: item.property_key,
                          target_type: 'filter_button',
                          click_type: 'view',
                          target_pos: 0
                        },
                        ctx: {
                          ...trackCtx,
                          tag_id: currentSelectTagId
                        }
                      });
                      if (isBoolItem) {
                        sortFilterProps?.applyExplorerFilterCallback(null, {
                          property_show_type: item.property_show_type,
                          filter_type: item.property_key,
                          filter_arr: [
                            {
                              property_show_type: item.property_show_type,
                              filter_type: item.property_key,
                              value_key: !isBoolItemSelected,
                              value_name: ''
                            }
                          ]
                        });
                      } else {
                        setSelectExplorer(item);
                        explorerFilterRef.current?.show();
                      }
                    }}
                    className={twMerge(
                      'rounded-[13px] bg-surface-100-hairline px-2.5 ml-2 flex items-center enki-body-2xs-medium h-[30px] whitespace-nowrap',
                      isBoolItem && isBoolItemSelected ? 'bg-[#B0D0FF4A]' : ''
                    )}>
                    {item.property_name}
                    {isBoolItem && isBoolItemSelected && <CheckmarkOutline fontSize={16} className="ml-1" />}
                    {!isBoolItem && (
                      <>
                        {selectedOutFilters && selectedOutFilters[item.property_key]?.length ? (
                          <span className="inline-block w-[14px] h-[14px] rounded-full !leading-[14px] enki-body-2xs-strong text-white bg-black text-center p-0.5 ml-1">
                            {selectedOutFilters[item.property_key]?.length}
                          </span>
                        ) : null}
                        <WeeeIcon
                          style={{ fontSize: 20 }}
                          className="text-surface-100-fg-default ml-1"
                          type="iconarrow-down"
                        />
                      </>
                    )}
                  </div>
                </ImpTracker>
              );
            })}
            {Object.values(selectedOutFilters || {})
              ?.filter((item, idx) => {
                const outFiltersKeys = outFilters.map(item => item.property_key);
                const selectedKeys = Object.keys(selectedOutFilters || {});
                if (isValidArray(item) && outFiltersKeys.includes(selectedKeys[idx])) {
                  return item[0]?.property_show_type !== FilterType.boolean;
                }
                return false;
              })
              ?.map(key => {
                if (key) {
                  return key?.map((dItem, dIndex) => {
                    return (
                      <div
                        key={`${dIndex}-${key}`}
                        onClick={() => {
                          //多选选择
                          if (
                            dItem.property_show_type === FilterType.multiple ||
                            dItem.property_show_type === FilterType.single
                          ) {
                            customButtonClick({
                              mod_nm: 'filter_buttons',
                              mod_pos: 2,
                              co: {
                                target_nm: dItem?.value_key,
                                target_type: 'filter_button',
                                target_pos: dIndex,
                                click_type: 'unselect'
                              },
                              ctx: trackCtx
                            });
                            sortFilterProps?.applyExplorerFilterCallback(null, {
                              property_show_type: dItem.property_show_type,
                              filter_type: dItem.filter_type,
                              filter_arr: key.filter(_sItem => {
                                return _sItem.value_key !== dItem.value_key;
                              })
                            });
                          }
                        }}
                        className="rounded-[13px] bg-[#EBF4FF] px-2.5 ml-2 flex items-center enki-body-2xs-medium text-[#1C2954] h-[30px] whitespace-nowrap">
                        {dItem?.value_name}
                        <WeeeIcon
                          style={{ fontSize: 20 }}
                          className="text-surface-100-fg-default ml-1"
                          type="iconTimes"
                        />
                      </div>
                    );
                  });
                }
              })}
            {/* 如果是单选需要全部放出来 可进行反选, todo */}
            {/* {showOutResult
                ?.filter(item => item.property_show_type === FilterType.boolean)
                ?.map((dItem, dIndex) => {
                  return (
                    <div
                      key={`${dIndex}-${dItem.value_key}`}
                      onClick={() => {
                        //单选选择
                      }}
                      className="rounded-400 bg-[#EBF4FF] px-2.5 ml-2 flex items-center enki-body-2xs text-[#1C2954] h-[30px] whitespace-nowrap">
                      {dItem?.property_name}
                      <WeeeIcon
                        style={{ fontSize: 12, marginTop: 2, zoom: 0.8 }}
                        className="text-surface-100-fg-default ml-1"
                        type="iconcheck-thin"
                      />
                    </div>
                  );
                })} */}
          </div>
        )}
      </div>
    );
  };

  const __renderMainCategoriesGlobalNavHeaderMode = () => {
    const customTab = (label: string, link: string, item, dataTestId?: string) => {
      let isItemSelected = false;
      if (mainCategory?.key === item.key) {
        isItemSelected = true;
      }
      const navTabProps = {
        label: label,
        dataTestId,
        onClick: event => {
          if (event?.stopPropagation) event.stopPropagation();
          if (event?.preventDefault) event.preventDefault();
          onCategoryClick?.(event);
          router.push(link);
        }
      };

      if (isItemSelected && bringSelectedAsFirstIfNeeded) {
        return <ForwardRefableNavTab ref={selectedNavbarUXRef} isSelected={true} {...navTabProps} />;
      } else {
        return <NavTab {...navTabProps} />;
      }
    };

    return (
      <>
        {mainCategoryList.map((item, sub) => {
          //lunar year requirement (old comment regarding +type === 4)
          const itemLink =
            +item.type === 4 ? relativeLinkIfQAEnv(item.url) : genCategoryHref(item.key, true, item) || '';
          const categoryIconItem = (
            <ImpTracker
              key={'i-' + item?.name + '-i-' + sub}
              className={twMerge(`flex i-${sub} flex-shrink-0`, sub === mainCategoryList?.length - 1 ? 'mr-400' : '')}
              impTrackingT2={() => {
                let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
                ellipseImpression({
                  mod_nm: CategoryMod.ellipse,
                  mod_pos: 0,
                  co: {
                    ellipse_label: item?.key,
                    ellipse_pos: sub,
                    ellipse_type: pageKey.replace('mweb_', ''),
                    is_new: item?.is_new,
                    url: router?.asPath
                  },
                  ctx: trackCtx
                });
              }}
              clickTrackingT2={() => {
                let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
                ellipseClick({
                  mod_nm: CategoryMod.ellipse,
                  mod_pos: 0,
                  co: {
                    ellipse_label: item?.key,
                    ellipse_pos: sub,
                    ellipse_type: pageKey.replace('mweb_', ''),
                    url: router?.asPath
                  },
                  ctx: {
                    filter_sub_category: item?.key,
                    catalogue_num: router.query.catalogue_num || 'all',
                    sort: router.query.sort || 'recommend',
                    filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                    ...trackCtx
                  }
                });
              }}>
              {customTab(item.name, itemLink, item, `wid-nav-tab-${item?.key ?? sub}`)}
            </ImpTracker>
          );

          return categoryIconItem;
        })}
      </>
    );
  };

  const __renderSubCategoriesGlobalNavHeaderMode = () => {
    const customTab = (label: string, link: string, item, dataTestId?: string) => {
      let isItemSelected = false;
      if (secondCategory?.num === item.num) {
        isItemSelected = true;
      }
      return (
        <a
          id={`secondCategory-${item.num}`}
          data-testid={dataTestId}
          className={twMerge(
            classnames(
              'h-[28px] text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-400 border border-solid border-surface-100-hairline',
              {
                'text-surface-100-bg border-primary-1  bg-btn-secondary-bg': isItemSelected,
                'bg-transparent': !isItemSelected
              }
            )
          )}
          onClick={event => {
            if (event?.stopPropagation) event.stopPropagation();
            if (event?.preventDefault) event.preventDefault();

            router.push(link);
          }}
          aria-label={`search second category ${item?.num}`}>
          {isItemSelected ? <h2 className="enki-body-2xs m-0">{item.name}</h2> : item.name}
        </a>
      );
    };

    return (
      <>
        {secondCategoryList.map((item, sub) => {
          const subCategoryLink = genCategoryHrefForPDPL2(item.num, item) || '';
          return (
            <ImpTracker
              key={`secondCategoryListItem:${sub}`}
              impTrackingT2={() => {
                filterBtnImpression({
                  mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                  // mod_pos: isFromBrand ? 0 : 1,
                  mod_pos: 0, //固定为0，pdp中模块计算从 tags之后开始增加index
                  co: {
                    button_nm: item?.num,
                    button_pos: sub + 1,
                    is_selected: secondCategory?.num === item.num,
                    url: router?.asPath
                  },
                  ctx: trackCtx
                });
              }}
              clickTrackingT2={() => {
                filterBtnClick({
                  mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                  mod_pos: isFromBrand ? 0 : 1,
                  co: {
                    button_nm: item?.num,
                    button_pos: sub + 1,
                    url: router?.asPath
                  },
                  ctx: {
                    filter_sub_category: router.query.filterCuisine || null,
                    catalogue_num: item?.num,
                    sort: router.query.sort || 'recommend',
                    filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                    ...trackCtx
                  }
                });
              }}>
              {customTab(item.name, subCategoryLink, item, `wid-nav-tab-${item?.num ?? sub}`)}
            </ImpTracker>
          );
        })}
      </>
    );
  };

  const checkShowSortFilter = () => {
    //如果有需要展示在外面的筛选项, 需要隐藏第一级的筛选项
    return outFilters.length < 1;
  };

  const renderFilterBadge = () => {
    if (changeCategory) {
      return null;
    }
    if (filterCount > 0) {
      if (showBadgeOnFilter) {
        return (
          <div className="min-w-[18px] box-border p-[3px] h-[18px] text-[13px] font-medium rounded-full bg-primary-1 text-white absolute flex items-center justify-center -top-[5px] left-5">
            {filterCount}
          </div>
        );
      }
      return (
        <div
          className="h-4 min-w-[16px] px-1 box-border rounded-full enki-body-2xs-strong text-surface-100-bg flex justify-center items-center -top-1 -right-1 absolute"
          style={{ background: mainCategory.color }}>
          {filterCount}
        </div>
      );
    }
    return null;
  };

  if (optimizeForGlobalHeader) {
    if (mainCategoryList?.length) {
      return __renderMainCategoriesGlobalNavHeaderMode();
    } else if (secondCategoryList?.length) {
      return __renderSubCategoriesGlobalNavHeaderMode();
    }
    return null;
  }
  return (
    <div
      className={twMerge(stickyL2Only ? 'pt-200' : 'sticky', 'z-[100] w-full top-12 bg-surface-100-bg')}
      style={{
        transition: 'all 0.3s ease-out',
        transform: 'translateY(0px)'
      }}
      id="category_filter"
      ref={categoryRef}
      data-role="main-category-body">
      {showMainCategory && (
        <div className="w-auto whitespace-nowrap overflow-hidden" data-role="main-category">
          <div className="pt-1" ref={scrollRef}>
            <MainCategory
              loadingMain={loadingMain}
              mainCategoryList={mainCategoryList}
              activeIndex={activeIndex}
              trackCtx={trackCtx}
              mainCategory={mainCategory}
              useClientMode={useClientMode}
              handleMainCategory={handleMainCategory}
              isNeedReplaceUrl={isNeedReplaceUrl}
              secondCategoryRef={secondCategoryRef}
              buildPathName={buildPathName}
              sortFilterProps={sortFilterProps}
            />
          </div>
        </div>
      )}
      <div
        className={twMerge(stickyL2Only ? 'sticky' : '', 'w-auto flex items-center pl-4 pt-4 pb-2')}
        data-role="second-category">
        {checkShowSortFilter() && (
          <div className="w-8 relative z-[1] rounded-full bg-surface-100-bg" data-role="second-category-filter">
            <div
              data-testid="btn-sub-category-filter"
              id="secondCategoryFilter"
              className={twMerge('w-8 h-8 relative flex items-center justify-center', filterBtnClassName)}
              onClick={() => {
                filterBtnClick({
                  mod_pos: 1,
                  co: {
                    button_nm: 'filter_buttons',
                    button_pos: 0
                  },
                  ctx: trackCtx
                });
                sortFilterRef.current?.show();
              }}>
              {changeCategory ? (
                <Skeleton.Button
                  style={{ width: '32px', position: 'relative', height: '32px', top: 0, right: 0 }}
                  shape="circle"
                />
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  data-testid="btn-sort-filter-popup"
                  className="rounded-400 bg-surface-100-hairline px-2 h-[30px] flex items-center">
                  <img className="w-4 h-4" src={secondFilterImg} alt="second filter img image" />
                </div>
              )}
              {renderFilterBadge()}
            </div>
          </div>
        )}
        <div
          className={twMerge(
            'box-border flex-1 grow overflow-x-scroll touch-auto relative -ml-2.5 pl-4',
            !checkShowSortFilter() && 'pl-2.5'
          )}
          ref={secondCategoryRef}>
          <div className="w-fit flex flex-nowrap items-center touch-auto">
            {changeCategory ? (
              <Skeleton.Button style={{ marginRight: '6px', borderRadius: '13px', height: '30px' }} />
            ) : (
              <ImpTracker
                height={30}
                impTrackingT2={() => {
                  filterBtnImpression({
                    mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                    mod_pos: isFromBrand ? 0 : 1,
                    co: {
                      button_nm: 'all',
                      button_pos: 0,
                      is_selected: secondCategory?.catalogue_num === '',
                      url: router?.asPath
                    },
                    ctx: trackCtx
                  });
                }}
                clickTrackingT2={() => {
                  filterBtnClick({
                    mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                    mod_pos: isFromBrand ? 0 : 1,
                    co: {
                      button_nm: 'all',
                      button_pos: 0,
                      url: router?.asPath
                    },
                    ctx: trackCtx
                  });
                }}>
                {useClientMode ? (
                  <a
                    data-testid="btn-sub-category-all"
                    className={twMerge(
                      classnames(
                        'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-400 border border-solid border-surface-100-hairline',
                        {
                          'text-surface-100-bg border-none': secondCategory?.catalogue_num === ''
                        }
                      )
                    )}
                    style={{
                      height: 28,
                      background: secondCategory?.catalogue_num === '' ? mainCategory?.color : 'transparent'
                    }}
                    href="#"
                    aria-label={`search all ${mainCategory}`}
                    onClick={e => {
                      e.preventDefault();
                      handleSecondCategory({ catalogue_num: '', catalogue_name: '' });
                    }}>
                    {t('search_all')}
                  </a>
                ) : (
                  <a
                    data-testid="btn-sub-category-all"
                    className={twMerge(
                      classnames(
                        'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-400 border border-solid border-surface-100-hairline',
                        {
                          'text-surface-100-bg border-none': secondCategory?.catalogue_num === ''
                        }
                      )
                    )}
                    style={{
                      height: 28,
                      background: secondCategory?.catalogue_num === '' ? mainCategory?.color : 'transparent'
                    }}
                    href="#"
                    aria-label={`search all ${mainCategory.name}`}
                    onClick={e => {
                      e.preventDefault();
                      router.push(genCategoryHref('', false, mainCategory) || '');
                    }}>
                    {t('search_all')}
                  </a>
                )}
              </ImpTracker>
            )}
            {changeCategory
              ? SKELETONS.map((item, index) => {
                  return (
                    <Skeleton.Button
                      key={'skeleton_second_item' + index}
                      style={{ marginRight: '6px', width: '100px', height: '30px', borderRadius: '13px' }}
                    />
                  );
                })
              : secondCategoryList.map((item, sub) => {
                  return (
                    <ImpTracker
                      key={`secondCategoryListItem:${sub}`}
                      impTrackingT2={() => {
                        filterBtnImpression({
                          mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                          mod_pos: isFromBrand ? 0 : 1,
                          co: {
                            button_nm: item?.catalogue_num,
                            button_pos: sub + 1,
                            is_selected: secondCategory.catalogue_num === item.catalogue_num,
                            url: router?.asPath
                          },
                          ctx: trackCtx
                        });
                      }}
                      clickTrackingT2={() => {
                        filterBtnClick({
                          mod_nm: isFromBrand ? BrandMod.filterBtn : CategoryMod.filterBtn,
                          mod_pos: isFromBrand ? 0 : 1,
                          co: {
                            button_nm: item?.catalogue_num,
                            button_pos: sub + 1,
                            url: router?.asPath
                          },
                          ctx: {
                            filter_sub_category: router.query.filterCuisine || null,
                            catalogue_num: item?.catalogue_num,
                            sort: router.query.sort || 'recommend',
                            filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                            ...trackCtx
                          }
                        });
                      }}>
                      {useClientMode ? (
                        <a
                          id={`secondCategory-${item?.catalogue_num}`}
                          data-testid={`btn-sub-category-${item?.catalogue_num}`}
                          className={twMerge(
                            classnames(
                              'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-400 border border-solid border-surface-100-hairline',
                              {
                                'text-surface-100-bg border-none': secondCategory?.catalogue_num === item.catalogue_num
                              }
                            )
                          )}
                          style={{
                            height: 28,
                            background:
                              secondCategory?.catalogue_num === item.catalogue_num ? mainCategory?.color : 'transparent'
                          }}
                          href="#"
                          aria-label={`search second category ${item?.catalogue_num}`}
                          onClick={event => {
                            event.preventDefault();
                            handleSecondCategory(item);
                          }}>
                          {secondCategory?.catalogue_num === item.catalogue_num ? (
                            <h2 className="enki-body-2xs m-0">{item.catalogue_name}</h2>
                          ) : (
                            item.catalogue_name
                          )}
                        </a>
                      ) : (
                        <a
                          id={`secondCategory-${item?.catalogue_num}`}
                          data-testid={`btn-sub-category-${item?.catalogue_num}`}
                          className={twMerge(
                            classnames(
                              'text-surface-100-fg-minor touch-auto flex justify-center items-center whitespace-nowrap mr-1.5 px-2.5 py-0 enki-body-2xs-medium rounded-400 border border-solid border-surface-100-hairline',
                              {
                                'text-surface-100-bg border-none': secondCategory?.catalogue_num === item.catalogue_num
                              }
                            )
                          )}
                          style={{
                            height: 28,
                            background:
                              secondCategory?.catalogue_num === item.catalogue_num ? mainCategory?.color : 'transparent'
                          }}
                          href="#"
                          aria-label={`search second category ${item?.catalogue_num}`}
                          onClick={event => {
                            if (props.secondCategoryPreventDefault) {
                              event.preventDefault();
                            } else {
                              event.currentTarget.scrollIntoView({ block: 'center', inline: 'center' });
                            }
                            event.preventDefault();
                            router.push(genCategoryHref(item.catalogue_num, false, item) || '');
                          }}>
                          {secondCategory?.catalogue_num === item.catalogue_num ? (
                            <h2 className="enki-body-2xs m-0">{item.catalogue_name}</h2>
                          ) : (
                            item.catalogue_name
                          )}
                        </a>
                      )}
                    </ImpTracker>
                  );
                })}
          </div>
        </div>
      </div>
      {/* Explorer store */}
      {renderExplorerStore()}
      <SortFilter {...sortFilterProps} trackCtx={trackCtx} ref={sortFilterRef} />
      <ExplorerFilter
        curFilter={selectExplorer}
        explorerFilterData={sortFilterProps?.explorerFilterData}
        selectedOutFilters={selectedOutFilters}
        applyExplorerFilterCallback={sortFilterProps?.applyExplorerFilterCallback}
        trackCtx={trackCtx}
        ref={explorerFilterRef}
      />
    </div>
  );
}

export default HorizontalSlideList;
